// Ops · Reports: totals, daily bars, by line, by shop, cancel reasons and the CSV download
// (docs/PHASE7-CONTRACT.md, contract.d.ts "Ops · Reports"). Pure (the download takes its fetch
// and token as arguments): node --test runs it.
import { ApiRequestError } from '@popcart/core/v1';
import { count, minutes, peso, percent } from '../../lib/format.js';
import { dayLabel, shortDay } from './period.js';

/** @typedef {import('@popcart/core/v1').OpsReport} OpsReport */
/** @typedef {{ from: string; to: string }} Period */

/** @param {Period} p */
export const reportKey = (p) => ['ops', 'report', p.from, p.to];

/** Customer copy: the mart line is "Grocery" (B3.10). */
export const LINE_NAME = /** @type {const} */ ({ fresh: 'Fresh', grill: 'Grill', mart: 'Grocery' });

/** Share of a total as a whole percent (0 when the total is 0). @param {number} part @param {number} total */
export function share(part, total) {
  return total > 0 ? Math.round((Math.max(0, part) / total) * 100) : 0;
}

/**
 * The KPI tiles, in reading order.
 * @param {OpsReport['totals']} t
 * @returns {{ label: string; value: string; detail: string }[]}
 */
export function totalsView(t) {
  return [
    { label: 'GMV', value: peso(t.gmvCentavos), detail: `${count(t.delivered)} delivered` },
    { label: 'Orders', value: count(t.orders), detail: `${count(t.cancelled)} cancelled` },
    { label: 'Average order', value: peso(t.aovCentavos), detail: 'delivered orders' },
    { label: 'Commission', value: peso(t.commissionCentavos), detail: 'from partner shops' },
    { label: 'Delivery fees', value: peso(t.deliveryFeesCentavos), detail: `+ ${peso(t.expressSurchargeCentavos)} Express` },
    { label: 'Refunds', value: peso(t.refundsCentavos), detail: `${peso(t.creditsIssuedCentavos)} in credits` },
    { label: 'On time', value: percent(t.onTimePct), detail: t.avgDeliveryMinutes === null ? 'no deliveries' : `${minutes(t.avgDeliveryMinutes)} on average` },
    { label: 'New customers', value: count(t.newCustomers), detail: 'first order in the period' },
  ];
}

/**
 * One CSS bar per day, scaled to the busiest day's GMV. Every 7th label (and the last) shows on
 * long periods so the axis stays readable.
 * @param {OpsReport['days']} days
 */
export function dailyBars(days) {
  const max = Math.max(0, ...days.map((d) => d.gmvCentavos));
  const every = days.length > 14 ? 7 : 1;
  return days.map((d, i) => ({
    day: d.day,
    pct: max > 0 ? Math.max(d.gmvCentavos > 0 ? 2 : 0, Math.round((d.gmvCentavos / max) * 100)) : 0,
    label: shortDay(d.day),
    showLabel: i % every === 0 || i === days.length - 1,
    title: `${dayLabel(d.day)}: ${peso(d.gmvCentavos)} from ${count(d.delivered)} delivered (${count(d.orders)} orders, ${count(d.cancelled)} cancelled)`,
  }));
}

/** @param {OpsReport['byLine']} rows */
export function lineRows(rows) {
  const total = rows.reduce((s, r) => s + Math.max(0, r.gmvCentavos), 0);
  return [...rows]
    .sort((a, b) => b.gmvCentavos - a.gmvCentavos)
    .map((r) => ({ line: r.line, name: LINE_NAME[r.line] ?? r.line, orders: count(r.orders), gmv: peso(r.gmvCentavos), pct: share(r.gmvCentavos, total) }));
}

/** Busiest first. @param {OpsReport['byShop']} rows */
export function shopRows(rows) {
  const total = rows.reduce((s, r) => s + Math.max(0, r.gmvCentavos), 0);
  return [...rows]
    .sort((a, b) => b.gmvCentavos - a.gmvCentavos || a.shopName.localeCompare(b.shopName))
    .map((r) => ({ id: r.shopId, name: r.shopName, orders: count(r.orders), gmv: peso(r.gmvCentavos), pct: share(r.gmvCentavos, total) }));
}

/** The API's cancel codes in plain words; anything else is a shop's own reason, shown as written. */
const CANCEL_CODES = /** @type {Record<string, string>} */ ({
  customer: 'Customer cancelled',
  payment_setup_failed: "Payment didn't go through",
  payment_failed: "Payment didn't go through",
  payment_expired: 'Payment not finished in time',
});
/** @param {string | null | undefined} reason */
export const cancelLabel = (reason) => (reason ? CANCEL_CODES[reason] ?? reason : 'No reason given');

/** Most common first. @param {OpsReport['cancelReasons']} rows */
export function cancelRows(rows) {
  const total = rows.reduce((s, r) => s + r.count, 0);
  return [...rows].sort((a, b) => b.count - a.count).map((r) => ({ reason: cancelLabel(r.reason), count: count(r.count), pct: share(r.count, total) }));
}

/** "popcart-report-2026-09-29-to-2026-10-05.csv". @param {Period} p */
export function csvFileName(p) {
  return `popcart-report-${p.from}-to-${p.to}.csv`;
}

/**
 * GET /api/v1/ops/reports.csv with the session's bearer token (the core client only speaks JSON).
 * Resolves to the CSV as a Blob; a non-2xx throws ApiRequestError like the client would.
 * @param {Period} p
 * @param {{ token: string | null; fetchImpl?: typeof fetch; baseUrl?: string; onUnauthorized?: () => void }} opts
 * @returns {Promise<Blob>}
 */
export async function fetchReportCsv(p, { token, fetchImpl, baseUrl = '', onUnauthorized }) {
  const doFetch = fetchImpl ?? ((/** @type {Parameters<typeof fetch>} */ ...a) => fetch(...a));
  const url = `${baseUrl.replace(/\/+$/, '')}/api/v1/ops/reports.csv?from=${encodeURIComponent(p.from)}&to=${encodeURIComponent(p.to)}`;
  /** @type {Record<string, string>} */
  const headers = { Accept: 'text/csv' };
  if (token) headers.Authorization = `Bearer ${token}`;
  let res;
  try {
    res = await doFetch(url, { method: 'GET', headers });
  } catch {
    throw new ApiRequestError(0, /** @type {any} */ ({ error: "Can't reach PopCart. Check your connection.", code: 'network' }));
  }
  if (!res.ok) {
    if (res.status === 401) onUnauthorized?.();
    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    throw new ApiRequestError(res.status, body);
  }
  return res.blob();
}
